import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@/components/ui/Tooltip';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { useAnalyticsStore } from '@/stores/analyticsStore';
import { AnalyticsFilterDrawer } from './AnalyticsFilterDrawer';
import { filterTriggerHover } from '@/components/ui/filterTriggerStyles';

/**
 * AnalyticsFilters Component
 *
 * Lean horizontal filter bar with two sections:
 * 1. Compare Toggle Pills (Off, Platforms, Channels, Period)
 * 2. Filters Button
 *
 * Platform/Channel/DateRange/TimeRange filters moved to drawer for cleaner UI.
 * All state managed via useAnalyticsStore()
 */
export const AnalyticsFilters = () => {
  const { t } = useTranslation('analytics');
  const store = useAnalyticsStore();
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);

  // Check if there are active filters (platform, channel, or non-default time range)
  const hasActiveFilters = store.platform !== null || store.channelId !== null || store.timeRange !== '30d';

  // Pill button style helper
  const pillClass = (active: boolean) =>
    `px-2 sm:px-3 h-9 sm:h-auto sm:py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 ${
      active
        ? 'bg-gray-900 text-white border border-gray-900'
        : `border border-gray-200 text-gray-600 bg-white ${filterTriggerHover}`
    }`;

  return (
    <>
      <div className="flex items-center justify-between gap-2 sm:gap-3">
        {/* Left: Compare Toggle */}
        <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
          <Tooltip content={t('tip_compare', { defaultValue: 'Switch between table view and comparison charts' })} side="bottom">
            <span className="hidden sm:inline text-xs text-gray-500 font-medium uppercase tracking-wide cursor-help">
              {t('compare_label', { defaultValue: 'Compare' })}
            </span>
          </Tooltip>

          <button type="button" onClick={() => store.setCompareMode('none')} className={pillClass(store.compareMode === 'none')}>
            {t('compare_off', { defaultValue: 'Off' })}
          </button>

          <Tooltip content={t('tip_compare_platforms', { defaultValue: 'Compare reach and engagement across your connected platforms' })} side="bottom">
            <button type="button" onClick={() => store.setCompareMode('platform')} className={pillClass(store.compareMode === 'platform')}>
              {t('compare_platforms', { defaultValue: 'Platforms' })}
            </button>
          </Tooltip>

          <Tooltip content={t('tip_compare_channels', { defaultValue: 'Compare reach and engagement across your connected channels' })} side="bottom">
            <button type="button" onClick={() => store.setCompareMode('channel')} className={pillClass(store.compareMode === 'channel')}>
              {t('compare_channels', { defaultValue: 'Channels' })}
            </button>
          </Tooltip>

          <Tooltip content={t('tip_compare_period', { defaultValue: 'Compare current period against the previous period of the same length' })} side="bottom">
            <button type="button" onClick={() => store.setCompareMode('period')} className={pillClass(store.compareMode === 'period')}>
              {t('compare_period', { defaultValue: 'Period' })}
            </button>
          </Tooltip>
        </div>

        {/* Right: Filters button only */}
        <button
          type="button"
          onClick={() => setIsFilterDrawerOpen(true)}
          className={`relative flex items-center justify-center gap-1.5 w-9 h-9 sm:w-auto sm:h-auto sm:px-3 sm:py-1.5 rounded-lg text-sm font-medium border border-gray-200 text-gray-600 bg-white transition-all duration-200 ${filterTriggerHover}`}
        >
          <Icon name={ICONS.FILTER} size="xs" />
          <span className="hidden sm:inline">{t('filters', { defaultValue: 'Filters' })}</span>
          {/* Active filter dot */}
          {hasActiveFilters && (
            <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-primary-500 rounded-full border-2 border-white" />
          )}
        </button>
      </div>

      <AnalyticsFilterDrawer isOpen={isFilterDrawerOpen} onClose={() => setIsFilterDrawerOpen(false)} />
    </>
  );
};
